import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Mail, Phone } from "lucide-react";
import Logo from "./Logo";
import { SERVICES } from "@/data/services";

const navLinks = [
  { to: "/services", label: "Services", hasMega: true },
  { to: "/industries", label: "Industries" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/sustainability", label: "Sustainability" },
  { to: "/insights", label: "Insights" },
  { to: "/resources", label: "Resources" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const location = useLocation();
  const [prevPath, setPrevPath] = useState(location.pathname);

  if (location.pathname !== prevPath) {
    setPrevPath(location.pathname);
    setOpen(false);
    setMegaOpen(false);
  }

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-xl border-b border-black/5 shadow-[0_1px_0_rgba(0,0,0,0.02)]" : "bg-white/70 backdrop-blur-md"
      }`}
    >
      {/* Top accent strip */}
      <div className="h-[3px] bg-accent" aria-hidden="true" />

      <div className="max-w-wide mx-auto px-6 md:px-10 lg:px-14 flex items-center justify-between h-[88px] gap-10">
        <Logo size="md" withBadge />

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8 ml-4 xl:ml-8">
          {navLinks.map((link) =>
            link.hasMega ? (
              <div
                key={link.to}
                className="relative"
                onMouseEnter={() => setMegaOpen(true)}
                onMouseLeave={() => setMegaOpen(false)}
              >
                <NavLink
                  to={link.to}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1 text-[14px] font-medium tracking-tight relative pb-1 ${
                      isActive ? "text-ink" : "text-ink/75 hover:text-ink"
                    } group`
                  }
                >
                  {link.label}
                  <ChevronDown className="h-3.5 w-3.5" strokeWidth={2.2} />
                  <span className="absolute left-0 right-0 -bottom-0.5 h-px bg-accent scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
                </NavLink>
                {megaOpen && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-5 w-[680px]"
                    data-testid="services-mega-menu"
                  >
                    <div className="bg-white border border-black/5 rounded-md shadow-[0_30px_80px_-20px_rgba(0,0,0,0.18)] p-7 grid grid-cols-2 gap-1 relative">
                      <span className="absolute -top-1 left-1/2 -translate-x-1/2 h-2 w-2 bg-accent rotate-45" aria-hidden="true" />
                      {SERVICES.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          className="group p-3 rounded-sm hover:bg-paper-cool transition-colors"
                          data-testid={`mega-service-${s.slug}`}
                        >
                          <div className="font-mono text-[10px] tracking-overline text-accent uppercase mb-1">
                            {s.overline}
                          </div>
                          <div className="font-display font-semibold text-[15px] text-ink group-hover:text-brand transition-colors">
                            {s.title}
                          </div>
                          <div className="text-[12px] text-muted-foreground mt-1 leading-snug line-clamp-2">
                            {s.short}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative text-[14px] font-medium tracking-tight pb-1 whitespace-nowrap ${
                    isActive ? "text-ink" : "text-ink/75 hover:text-ink"
                  } group`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    <span
                      className={`absolute left-0 right-0 -bottom-0.5 h-px bg-accent origin-left transition-transform duration-300 ${
                        isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-5">
          <div className="hidden xl:flex flex-col items-end gap-1 pr-5 mr-1 border-r border-black/10 leading-tight">
            <a
              href="mailto:Hello@calmbreeze.co.uk"
              data-testid="header-cta-email"
              className="inline-flex items-center gap-2 text-[13px] font-medium text-ink/80 hover:text-ink transition-colors"
            >
              <Mail className="h-3.5 w-3.5 text-accent shrink-0" />
              Hello@calmbreeze.co.uk
            </a>
            <a
              href="tel:+441895919333"
              data-testid="header-cta-phone"
              className="inline-flex items-center gap-2 text-[13px] font-semibold text-ink hover:text-brand transition-colors"
            >
              <Phone className="h-3.5 w-3.5 text-accent shrink-0" />
              01895 919 333
            </a>
          </div>
          <Link
            to="/contact"
            data-testid="header-cta-quote"
            className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 border border-ink text-ink text-[13px] font-semibold rounded-sm hover:bg-ink hover:text-white transition-colors whitespace-nowrap"
          >
            Get a quote
          </Link>
          <button
            className="lg:hidden p-2 -mr-2 text-ink"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden bg-white border-t border-black/5"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 space-y-4">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `block text-[18px] font-display font-semibold ${
                    isActive ? "text-accent" : "text-ink"
                  }`
                }
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="inline-flex mt-4 px-5 py-3 border border-ink text-ink text-[14px] font-semibold rounded-sm hover:bg-ink hover:text-white transition-colors"
              data-testid="mobile-cta-quote"
            >
              Get a quote
            </Link>
            <div className="pt-4 flex flex-wrap gap-x-6 gap-y-2">
              <a href="tel:+441895919333" className="inline-flex items-center gap-2 text-[14px] font-semibold text-ink" data-testid="mobile-phone">
                <Phone className="h-3.5 w-3.5 text-accent" /> 01895 919 333
              </a>
              <a href="mailto:Hello@calmbreeze.co.uk" className="inline-flex items-center gap-2 text-[14px] font-medium text-ink/80">
                <Mail className="h-3.5 w-3.5 text-accent" /> Hello@calmbreeze.co.uk
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
